/**
 * Il banco di misura (00-PERIMETRO §7, 07-PRESTAZIONI §8).
 *
 * «Una prestazione che non si misura peggiora sempre, e non se ne accorge nessuno finché
 * non è tardi.» I numeri si prendono sul dispositivo, non sul computer di chi scrive.
 *
 * **Mediana e 95° percentile, non la media.** La media di cento spostamenti del fuoco in
 * cui novantacinque sono a 20 ms e cinque a 400 fa 39 ms e sembra buona; l'utente sente i
 * cinque. Il numero che decide è il 95°.
 */

export interface LatencySummary {
  readonly count: number;
  readonly medianMs: number;
  readonly p95Ms: number;
  readonly maxMs: number;
}

export class LatencyRecorder {
  private readonly samples: number[] = [];

  constructor(private readonly capacity = 2_000) {}

  add(valueMs: number): void {
    if (!Number.isFinite(valueMs) || valueMs < 0) return;
    this.samples.push(valueMs);
    if (this.samples.length > this.capacity) this.samples.shift();
  }

  reset(): void {
    this.samples.length = 0;
  }

  get size(): number {
    return this.samples.length;
  }

  summary(): LatencySummary {
    return summarize(this.samples);
  }
}

/** Percentile con interpolazione lineare (il metodo «linear» di R type 7). */
export function percentile(values: ReadonlyArray<number>, fraction: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) return sorted[0] as number;
  const position = (sorted.length - 1) * fraction;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  const low = sorted[lower] as number;
  if (lower === upper) return low;
  const high = sorted[upper] as number;
  return low + (high - low) * (position - lower);
}

export function summarize(values: ReadonlyArray<number>): LatencySummary {
  if (values.length === 0) return { count: 0, medianMs: 0, p95Ms: 0, maxMs: 0 };
  return {
    count: values.length,
    medianMs: percentile(values, 0.5),
    p95Ms: percentile(values, 0.95),
    maxMs: Math.max(...values),
  };
}

/**
 * Le cornici perse.
 *
 * Non si contano i fotogrammi al secondo — su una TV la media è inutile perché una
 * caduta di due decimi si perde dentro un secondo buono. Si contano gli intervalli fra
 * una cornice e la successiva che superano il budget: ognuno di quelli è una cornice che
 * non c'è stata.
 */
export const FRAME_BUDGET_MS = 1000 / 60;

export interface FrameSummary {
  readonly frames: number;
  readonly droppedFrames: number;
  readonly longestGapMs: number;
  readonly effectiveFps: number;
}

export class FrameRecorder {
  private frames = 0;
  private droppedFrames = 0;
  private longestGapMs = 0;
  private totalMs = 0;
  private lastTimestampMs: number | null = null;

  tick(timestampMs: number): void {
    if (this.lastTimestampMs !== null) {
      const gap = timestampMs - this.lastTimestampMs;
      this.frames += 1;
      this.totalMs += gap;
      if (gap > this.longestGapMs) this.longestGapMs = gap;
      // Un intervallo di 33 ms ha mangiato una cornice, uno di 50 ne ha mangiate due.
      this.droppedFrames += Math.max(0, Math.round(gap / FRAME_BUDGET_MS) - 1);
    }
    this.lastTimestampMs = timestampMs;
  }

  reset(): void {
    this.frames = 0;
    this.droppedFrames = 0;
    this.longestGapMs = 0;
    this.totalMs = 0;
    this.lastTimestampMs = null;
  }

  summary(): FrameSummary {
    return {
      frames: this.frames,
      droppedFrames: this.droppedFrames,
      longestGapMs: this.longestGapMs,
      effectiveFps: this.totalMs > 0 ? (this.frames / this.totalMs) * 1000 : 0,
    };
  }
}

/**
 * Tempo da OK a primo fotogramma, **e se il canale era acceso o spento**: senza questa
 * distinzione la media non significa niente (06-RIPRODUZIONE §10).
 */
export interface FirstFrameSample {
  readonly ms: number;
  readonly channelWasWarm: boolean;
}

export interface Phase1Report {
  readonly focusLatency: LatencySummary;
  readonly frames: FrameSummary;
  readonly firstFrameWarm: LatencySummary;
  readonly firstFrameCold: LatencySummary;
  readonly channelCount: number;
}

/** I bersagli di 00-PERIMETRO §7 e 07-PRESTAZIONI §6, in un posto solo. */
export const PHASE1_TARGETS = {
  focusLatencyP95Ms: 100,
  droppedFrames: 0,
  firstFrameWarmMs: 2_000,
  firstFrameColdMs: 12_000,
} as const;

export function meetsFocusTarget(summary: LatencySummary): boolean {
  return summary.count > 0 && summary.p95Ms < PHASE1_TARGETS.focusLatencyP95Ms;
}
